/**
 * Ingestion Config Admin Page
 * ============================
 * /admin/ingestion-config
 * 
 * Allows managing brand and competitor ingestion configuration
 * without code changes.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import GlobalHeader from '../../components/GlobalHeader';
import { useTenant } from '../../contexts/TenantContext';
import {
  Settings, Plus, Trash2, Save, RefreshCw, Search,
  Hash, Tag, ShoppingBag, Globe, Check, X, Edit2,
  Database, Zap, Store, Play, Clock, AlertCircle,
  CheckCircle2, Loader2, ChevronRight, Flag, Download,
  TrendingUp, Filter, Calendar, User, ExternalLink
} from 'lucide-react';

// ============================================================================
// TYPES
// ============================================================================

interface BrandSource {
  id: string;
  company_id: string;
  brand_key: string;
  type: string;
  value: string;
  source: string;
  active: boolean;
  priority: number;
  created_at: string;
}

interface CompetitorSource {
  id: string;
  company_id: string;
  competitor_key: string;
  brand_name: string;
  retailer: string;
  category_or_product_url: string;
  product_asin: string | null;
  category: string | null;
  price_range: string | null;
  active: boolean;
  priority: number;
}

interface FeedbackStats {
  total_feedback: number;
  relevant_count: number;
  irrelevant_count: number;
}

interface FeedbackEntry {
  id: string;
  mention_id: string;
  company_id: string;
  user_email: string;
  is_relevant: boolean;
  feedback_reason: string;
  created_at: string;
  raw_text: string;
  source: string;
}

interface FeedbackData {
  stats: FeedbackStats;
  recentFeedback: FeedbackEntry[];
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

interface SyncResult {
  success: boolean;
  message: string;
  totalRecordsImported: number;
  jobs: Array<{
    source: string;
    status: string;
    recordsImported: number;
    errors: number;
  }>;
}

async function runSocialSync(companyId: string, sources: string[]): Promise<SyncResult> {
  const response = await fetch('/api/ingestion/social/run', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, sources, async: false })
  });
  if (!response.ok) throw new Error('Failed to run social sync');
  return response.json();
}

async function runCompetitorSync(companyId: string): Promise<SyncResult> {
  const response = await fetch('/api/ingestion/competitor/run', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, async: false })
  });
  if (!response.ok) throw new Error('Failed to run competitor sync');
  return response.json();
}

async function runCreativeSync(companyId: string): Promise<SyncResult> {
  const response = await fetch('/api/ingestion/creative/run', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, async: false })
  });
  if (!response.ok) throw new Error('Failed to run creative sync');
  return response.json();
}

async function fetchBrandSources(): Promise<{ sources: BrandSource[] }> {
  const response = await fetch('/api/ingestion/config/brands-api');
  if (!response.ok) throw new Error('Failed to fetch brand sources');
  return response.json();
}

async function fetchCompetitorSources(): Promise<{ sources: CompetitorSource[] }> {
  const response = await fetch('/api/ingestion/config/competitors-api');
  if (!response.ok) throw new Error('Failed to fetch competitor sources');
  return response.json();
}

async function saveBrandSource(source: Partial<BrandSource>): Promise<any> {
  const response = await fetch('/api/ingestion/config/brands-api', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(source)
  });
  if (!response.ok) throw new Error('Failed to save brand source');
  return response.json();
}

async function saveCompetitorSource(source: Partial<CompetitorSource>): Promise<any> {
  const response = await fetch('/api/ingestion/config/competitors-api', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(source)
  });
  if (!response.ok) throw new Error('Failed to save competitor source');
  return response.json();
}

async function fetchFeedback(companyId: string): Promise<FeedbackData> {
  const response = await fetch(`/api/social-listening/feedback?companyId=${companyId}`);
  if (!response.ok) throw new Error('Failed to fetch feedback');
  return response.json();
}

async function fetchMLStatus(companyId: string) {
  const response = await fetch(`/api/ml/classify-relevance?companyId=${companyId}`);
  if (!response.ok) throw new Error('Failed to fetch ML status');
  return response.json();
}

// ============================================================================
// SYNC NOW SECTION
// ============================================================================

function SyncNowSection() {
  const { companyId: rawCompanyId } = useTenant();
  // Use Buttah's company ID as fallback if not loaded yet
  const companyId = rawCompanyId || '00000000-0000-0000-0000-000000000001';
  const [syncStatus, setSyncStatus] = useState<Record<string, {
    loading: boolean;
    result?: SyncResult;
    error?: string;
    lastRun?: Date;
  }>>({});

  const runSync = async (type: 'social' | 'competitor' | 'creative', sources?: string[]) => {
    setSyncStatus(prev => ({ ...prev, [type]: { loading: true } }));
    
    try {
      let result: SyncResult;
      
      if (type === 'social' && sources) {
        result = await runSocialSync(companyId, sources);
      } else if (type === 'competitor') {
        result = await runCompetitorSync(companyId);
      } else if (type === 'creative') {
        result = await runCreativeSync(companyId);
      } else {
        throw new Error('Invalid sync type');
      }
      
      setSyncStatus(prev => ({ 
        ...prev, 
        [type]: { loading: false, result, lastRun: new Date() } 
      }));
    } catch (error: any) {
      setSyncStatus(prev => ({ 
        ...prev, 
        [type]: { loading: false, error: error.message, lastRun: new Date() } 
      }));
    }
  };

  const syncButtons = [
    {
      id: 'tiktok',
      label: 'TikTok',
      icon: '📱',
      color: 'from-pink-500 to-rose-600',
      sources: ['tiktok'],
      type: 'social' as const,
      description: 'Hashtag mentions'
    },
    {
      id: 'instagram',
      label: 'Instagram',
      icon: '📸',
      color: 'from-purple-500 to-pink-600',
      sources: ['instagram'],
      type: 'social' as const,
      description: 'Hashtag posts'
    },
    {
      id: 'reddit',
      label: 'Reddit',
      icon: '💬',
      color: 'from-orange-500 to-red-600',
      sources: ['reddit'],
      type: 'social' as const,
      description: 'Subreddit mentions'
    },
    {
      id: 'amazon',
      label: 'Amazon Reviews',
      icon: '📦',
      color: 'from-yellow-500 to-orange-600',
      sources: ['amazon'],
      type: 'social' as const,
      description: 'Product reviews'
    },
    {
      id: 'all_social',
      label: 'All Social',
      icon: '🌐',
      color: 'from-blue-500 to-indigo-600',
      sources: ['tiktok', 'instagram', 'reddit', 'amazon'],
      type: 'social' as const,
      description: 'Run all at once'
    },
    {
      id: 'competitor',
      label: 'Competitors',
      icon: '🏪',
      color: 'from-emerald-500 to-teal-600',
      sources: undefined,
      type: 'competitor' as const,
      description: 'Products & pricing'
    }
  ];

  return (
    <div className="bg-gradient-to-br from-gray-800/70 to-gray-900/70 rounded-xl border border-gray-700/50 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-lg">
            <Play className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Sync Now</h3>
            <p className="text-sm text-gray-400">Run ingestion on-demand for any source</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Clock className="w-4 h-4" />
          <span>Weekly auto-sync enabled</span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {syncButtons.map(btn => {
          const status = syncStatus[btn.id];
          const isLoading = status?.loading;
          const hasResult = status?.result;
          const hasError = status?.error;
          
          return (
            <button
              key={btn.id}
              onClick={() => runSync(btn.type, btn.sources)}
              disabled={isLoading}
              className={`
                relative p-4 rounded-xl border transition-all duration-200
                ${isLoading 
                  ? 'border-gray-600 bg-gray-800/50 cursor-wait' 
                  : hasError 
                    ? 'border-red-500/50 bg-red-500/10 hover:bg-red-500/20'
                    : hasResult
                      ? 'border-green-500/50 bg-green-500/10 hover:bg-green-500/20'
                      : 'border-gray-600/50 bg-gray-800/30 hover:bg-gray-700/50 hover:border-gray-500'
                }
              `}
            >
              <div className="text-2xl mb-2">{btn.icon}</div>
              <div className="text-sm font-medium text-white">{btn.label}</div>
              <div className="text-xs text-gray-500 mt-1">{btn.description}</div>
              
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80 rounded-xl">
                  <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
                </div>
              )}
              
              {hasResult && !isLoading && (
                <div className="absolute top-2 right-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                </div>
              )}
              
              {hasError && !isLoading && (
                <div className="absolute top-2 right-2">
                  <AlertCircle className="w-4 h-4 text-red-400" />
                </div>
              )}
              
              {hasResult && !isLoading && (
                <div className="mt-2 text-xs text-green-400">
                  +{hasResult.totalRecordsImported} records
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Recent Sync Results */}
      {Object.entries(syncStatus).some(([_, s]) => s.result || s.error) && (
        <div className="mt-6 space-y-3">
          {/* Success banner with link to Social Listening */}
          {Object.values(syncStatus).some(s => s.result && s.result.totalRecordsImported > 0) && (
            <div className="p-4 bg-green-900/20 border border-green-700/50 rounded-lg flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-medium text-green-300 mb-1">Sync completed successfully!</div>
                <div className="text-xs text-green-400/70 mb-2">
                  View results and sentiment analysis in Social Listening
                </div>
                <a 
                  href="/agents/social-listening" 
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-green-400 hover:text-green-300 transition-colors"
                >
                  <span>View Social Listening</span>
                  <ChevronRight className="w-3 h-3" />
                </a>
              </div>
            </div>
          )}

          <div className="p-4 bg-gray-900/50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-300 mb-3">Recent Sync Results</h4>
            <div className="space-y-2">
              {Object.entries(syncStatus)
                .filter(([_, s]) => s.result || s.error)
                .map(([key, status]) => (
                  <div key={key} className="flex items-center justify-between text-sm">
                    <span className="text-gray-400 capitalize">{key.replace('_', ' ')}</span>
                    {status.result ? (
                      <span className="text-green-400">
                        ✓ {status.result.totalRecordsImported} records imported
                      </span>
                    ) : (
                      <span className="text-red-400">✗ {status.error}</span>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// COMPONENTS
// ============================================================================

function BrandSourcesSection() {
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSource, setNewSource] = useState({
    brand_key: 'buttah',
    type: 'social_query',
    value: '',
    source: 'tiktok',
    active: true,
    priority: 0
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['brandSources'],
    queryFn: fetchBrandSources
  });

  const mutation = useMutation({
    mutationFn: saveBrandSource,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brandSources'] });
      setShowAddForm(false);
      setNewSource({ ...newSource, value: '' });
    }
  });

  const typeIcons: Record<string, React.ReactNode> = {
    social_query: <Search className="w-4 h-4" />,
    hashtag: <Hash className="w-4 h-4" />,
    asin: <ShoppingBag className="w-4 h-4" />,
    subreddit: <Globe className="w-4 h-4" />
  };

  const sourceColors: Record<string, string> = {
    tiktok: 'bg-pink-500/20 text-pink-400',
    instagram: 'bg-purple-500/20 text-purple-400',
    reddit: 'bg-orange-500/20 text-orange-400',
    amazon_reviews: 'bg-yellow-500/20 text-yellow-400'
  };

  if (isLoading) return <div className="text-gray-400">Loading brand sources...</div>;
  if (error) return <div className="text-red-400">Error loading brand sources</div>;

  const groupedByType: Record<string, BrandSource[]> = {};
  data?.sources.forEach(s => {
    if (!groupedByType[s.type]) groupedByType[s.type] = [];
    groupedByType[s.type].push(s);
  });

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-500/20 rounded-lg">
            <Tag className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Brand Sources</h3>
            <p className="text-sm text-gray-400">Search queries, hashtags, and ASINs for monitoring</p>
          </div>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Source
        </button>
      </div>

      {showAddForm && (
        <div className="mb-6 p-4 bg-gray-900/50 rounded-lg border border-gray-600/50">
          <div className="grid grid-cols-4 gap-4 mb-4">
            <select
              value={newSource.type}
              onChange={e => setNewSource({ ...newSource, type: e.target.value })}
              className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white"
            >
              <option value="social_query">Search Query</option>
              <option value="hashtag">Hashtag</option>
              <option value="asin">ASIN</option>
              <option value="subreddit">Subreddit</option>
            </select>
            <select
              value={newSource.source}
              onChange={e => setNewSource({ ...newSource, source: e.target.value })}
              className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white"
            >
              <option value="tiktok">TikTok</option>
              <option value="instagram">Instagram</option>
              <option value="reddit">Reddit</option>
              <option value="amazon_reviews">Amazon Reviews</option>
            </select>
            <input
              type="text"
              value={newSource.value}
              onChange={e => setNewSource({ ...newSource, value: e.target.value })}
              placeholder="Enter value..."
              className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white"
            />
            <button
              onClick={() => mutation.mutate(newSource)}
              disabled={!newSource.value || mutation.isPending}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 rounded-lg text-white text-sm transition-colors"
            >
              <Save className="w-4 h-4" />
              {mutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {Object.entries(groupedByType).map(([type, sources]) => (
          <div key={type}>
            <div className="flex items-center gap-2 mb-2">
              {typeIcons[type]}
              <span className="text-sm font-medium text-gray-300 capitalize">
                {type.replace('_', ' ')}s ({sources.length})
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {sources.map(source => (
                <div
                  key={source.id}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                    source.active ? sourceColors[source.source] || 'bg-gray-600/50 text-gray-300' : 'bg-gray-700/30 text-gray-500 line-through'
                  }`}
                >
                  <span>{source.value}</span>
                  <span className="text-xs opacity-60">({source.source})</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-700/50 text-sm text-gray-500">
        Total: {data?.sources.length || 0} sources configured
      </div>
    </div>
  );
}

function CompetitorSourcesSection() {
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSource, setNewSource] = useState({
    competitor_key: '',
    brand_name: '',
    retailer: 'amazon',
    category_or_product_url: '',
    product_asin: '',
    category: '',
    price_range: 'mid',
    active: true,
    priority: 0
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['competitorSources'],
    queryFn: fetchCompetitorSources
  });

  const mutation = useMutation({
    mutationFn: saveCompetitorSource,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competitorSources'] });
      setShowAddForm(false);
      setNewSource({ ...newSource, competitor_key: '', brand_name: '', category_or_product_url: '', product_asin: '' });
    }
  });

  const retailerColors: Record<string, string> = {
    amazon: 'bg-orange-500/20 text-orange-400',
    ulta: 'bg-pink-500/20 text-pink-400',
    sephora: 'bg-red-500/20 text-red-400',
    target: 'bg-red-600/20 text-red-300',
    walmart: 'bg-blue-500/20 text-blue-400'
  };

  if (isLoading) return <div className="text-gray-400">Loading competitor sources...</div>;
  if (error) return <div className="text-red-400">Error loading competitor sources</div>;

  const groupedByBrand: Record<string, CompetitorSource[]> = {};
  data?.sources.forEach(s => {
    if (!groupedByBrand[s.brand_name]) groupedByBrand[s.brand_name] = [];
    groupedByBrand[s.brand_name].push(s);
  });

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <Store className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Competitor Sources</h3>
            <p className="text-sm text-gray-400">Product URLs to monitor for competitor intelligence</p>
          </div>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-sm transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Competitor
        </button>
      </div>

      {showAddForm && (
        <div className="mb-6 p-4 bg-gray-900/50 rounded-lg border border-gray-600/50">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <input
              type="text"
              value={newSource.brand_name}
              onChange={e => setNewSource({ 
                ...newSource, 
                brand_name: e.target.value,
                competitor_key: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '_')
              })}
              placeholder="Brand Name (e.g., SheaMoisture)"
              className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white"
            />
            <select
              value={newSource.retailer}
              onChange={e => setNewSource({ ...newSource, retailer: e.target.value })}
              className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white"
            >
              <option value="amazon">Amazon</option>
              <option value="ulta">Ulta</option>
              <option value="sephora">Sephora</option>
              <option value="target">Target</option>
              <option value="walmart">Walmart</option>
            </select>
            <input
              type="text"
              value={newSource.category_or_product_url}
              onChange={e => setNewSource({ ...newSource, category_or_product_url: e.target.value })}
              placeholder="Product URL (e.g., https://www.amazon.com/dp/B00MWB0ZGE)"
              className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white col-span-2"
            />
            <input
              type="text"
              value={newSource.product_asin}
              onChange={e => setNewSource({ ...newSource, product_asin: e.target.value })}
              placeholder="ASIN (optional)"
              className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white"
            />
            <select
              value={newSource.price_range}
              onChange={e => setNewSource({ ...newSource, price_range: e.target.value })}
              className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white"
            >
              <option value="budget">Budget</option>
              <option value="mid">Mid-Range</option>
              <option value="premium">Premium</option>
              <option value="luxury">Luxury</option>
            </select>
          </div>
          <button
            onClick={() => mutation.mutate(newSource)}
            disabled={!newSource.brand_name || !newSource.category_or_product_url || mutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 rounded-lg text-white text-sm transition-colors"
          >
            <Save className="w-4 h-4" />
            {mutation.isPending ? 'Saving...' : 'Save Competitor'}
          </button>
        </div>
      )}

      <div className="space-y-4">
        {Object.entries(groupedByBrand).map(([brand, sources]) => (
          <div key={brand} className="p-4 bg-gray-900/30 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium text-white">{brand}</span>
              <span className={`text-xs px-2 py-1 rounded ${sources[0].price_range === 'budget' ? 'bg-green-500/20 text-green-400' : sources[0].price_range === 'premium' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}`}>
                {sources[0].price_range}
              </span>
            </div>
            <div className="space-y-2">
              {sources.map(source => (
                <div key={source.id} className="flex items-center gap-3 text-sm">
                  <span className={`px-2 py-0.5 rounded text-xs ${retailerColors[source.retailer] || 'bg-gray-600/50 text-gray-300'}`}>
                    {source.retailer}
                  </span>
                  <a 
                    href={source.category_or_product_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:underline truncate max-w-md"
                  >
                    {source.category_or_product_url}
                  </a>
                  {source.product_asin && (
                    <span className="text-gray-500">({source.product_asin})</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-700/50 text-sm text-gray-500">
        Total: {data?.sources.length || 0} competitor products configured
      </div>
    </div>
  );
}

// ============================================================================
// FEEDBACK MANAGEMENT SECTION
// ============================================================================

function FeedbackManagementSection() {
  const { companyId: rawCompanyId } = useTenant();
  // Use Buttah's company ID as fallback if not loaded yet
  const companyId = rawCompanyId || '00000000-0000-0000-0000-000000000001';
  const [filterRelevance, setFilterRelevance] = useState<'all' | 'relevant' | 'irrelevant' | 'sentiment'>('all');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['social-feedback', companyId],
    queryFn: () => fetchFeedback(companyId),
    enabled: !!companyId
  });

  const { data: mlStatus } = useQuery({
    queryKey: ['ml-status', companyId],
    queryFn: () => fetchMLStatus(companyId),
    enabled: !!companyId,
    staleTime: 60 * 1000
  });

  const handleExportCSV = () => {
    if (!data?.recentFeedback) return;

    const headers = ['ID', 'Mention Text', 'Source', 'Is Relevant', 'Original Sentiment', 'Corrected Sentiment', 'Reason', 'User', 'Created At'];
    const rows = data.recentFeedback.map((f: any) => [
      f.id,
      f.raw_text?.replace(/"/g, '""') || '',
      f.source,
      f.is_relevant === null ? '' : (f.is_relevant ? 'YES' : 'NO'),
      f.original_sentiment || '',
      f.corrected_sentiment || '',
      f.feedback_reason,
      f.user_email,
      new Date(f.created_at).toISOString()
    ]);

    const csv = [
      headers.join(','),
      ...rows.map((row: any) => row.map((cell: any) => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `social-listening-ml-training-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const stats = data?.stats;
  const feedback = data?.recentFeedback || [];
  const sentimentCorrections = parseInt(stats?.sentiment_corrections) || 0;
  
  const filteredFeedback = feedback.filter((f: any) => {
    if (filterRelevance === 'relevant') return f.is_relevant === true;
    if (filterRelevance === 'irrelevant') return f.is_relevant === false;
    if (filterRelevance === 'sentiment') return f.corrected_sentiment !== null;
    return true;
  });

  // Total training samples = relevance labels + sentiment corrections
  const totalTrainingSamples = (parseInt(stats?.total_feedback) || 0) + sentimentCorrections;
  
  const mlReadiness = stats ? {
    current: totalTrainingSamples,
    minimum: 100,
    good: 500,
    excellent: 1000,
    percentage: Math.min((totalTrainingSamples / 1000) * 100, 100)
  } : null;

  // ML Model status
  const relevanceModelReady = mlStatus?.status?.relevance?.readyToTrain;
  const sentimentModelReady = mlStatus?.status?.sentiment?.readyToTrain;
  const relevanceModelTrained = mlStatus?.status?.relevance?.isAvailable;
  const sentimentModelTrained = mlStatus?.status?.sentiment?.isAvailable;

  if (isLoading) {
    return (
      <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 p-12 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mb-4"></div>
        <p className="text-gray-400">Loading feedback data...</p>
      </div>
    );
  }

  if (error || !stats) {
    return null; // Hide section if no data yet
  }

  if (stats.total_feedback === 0) {
    return null; // Hide section if no feedback yet
  }

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <Flag className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">ML Training Data</h3>
            <p className="text-sm text-gray-400">User feedback for relevance & sentiment classification</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={handleExportCSV}
            disabled={!data?.recentFeedback?.length}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 rounded-lg text-white text-sm transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        <div className="bg-gray-900/50 rounded-lg p-4">
          <div className="text-2xl font-bold text-white">{totalTrainingSamples}</div>
          <div className="text-xs text-gray-500">Total Labels</div>
        </div>
        <div className="bg-green-900/20 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-400">{stats.relevant_count}</div>
          <div className="text-xs text-gray-500">Relevant</div>
        </div>
        <div className="bg-red-900/20 rounded-lg p-4">
          <div className="text-2xl font-bold text-red-400">{stats.irrelevant_count}</div>
          <div className="text-xs text-gray-500">Irrelevant</div>
        </div>
        <div className="bg-amber-900/20 rounded-lg p-4">
          <div className="text-2xl font-bold text-amber-400">{sentimentCorrections}</div>
          <div className="text-xs text-gray-500">Sentiment Fixes</div>
        </div>
        <div className="bg-purple-900/20 rounded-lg p-4">
          <div className="text-2xl font-bold text-purple-400">{mlReadiness ? Math.round(mlReadiness.percentage) : 0}%</div>
          <div className="text-xs text-gray-500">ML Ready</div>
        </div>
      </div>

      {/* Progress Bar */}
      {mlReadiness && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2 text-sm">
            <span className="text-gray-400">Training Data Progress</span>
            <span className="text-gray-400">{mlReadiness.current} / {mlReadiness.excellent}</span>
          </div>
          <div className="relative h-2 bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500"
              style={{ width: `${mlReadiness.percentage}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-xs">
            <span className={mlReadiness.current >= mlReadiness.minimum ? 'text-green-400' : 'text-gray-500'}>
              Min: {mlReadiness.minimum}
            </span>
            <span className={mlReadiness.current >= mlReadiness.good ? 'text-green-400' : 'text-gray-500'}>
              Good: {mlReadiness.good}
            </span>
            <span className={mlReadiness.current >= mlReadiness.excellent ? 'text-green-400' : 'text-gray-500'}>
              Excellent: {mlReadiness.excellent}
            </span>
          </div>
        </div>
      )}

      {/* ML Models Status */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {/* Relevance Model */}
        <div className={`rounded-lg p-4 border ${relevanceModelTrained ? 'bg-green-900/20 border-green-700/50' : relevanceModelReady ? 'bg-amber-900/20 border-amber-700/50' : 'bg-gray-900/50 border-gray-700/50'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-white">Relevance Classifier</span>
            {relevanceModelTrained ? (
              <span className="px-2 py-0.5 bg-green-900/50 text-green-400 text-xs rounded">Trained ✓</span>
            ) : relevanceModelReady ? (
              <span className="px-2 py-0.5 bg-amber-900/50 text-amber-400 text-xs rounded">Ready to Train</span>
            ) : (
              <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded">Collecting Data</span>
            )}
          </div>
          
          {/* Relevance Progress Bar */}
          {!relevanceModelTrained && (
            <div className="mb-2">
              <div className="flex justify-between text-[10px] text-gray-500 mb-1">
                <span>{stats.total_feedback || 0} / 500 labels</span>
                <span>{Math.min(100, Math.round(((stats.total_feedback || 0) / 500) * 100))}%</span>
              </div>
              <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    (stats.total_feedback || 0) >= 500 ? 'bg-green-500' : 
                    (stats.total_feedback || 0) >= 100 ? 'bg-amber-500' : 'bg-purple-500'
                  }`}
                  style={{ width: `${Math.min(100, ((stats.total_feedback || 0) / 500) * 100)}%` }}
                />
              </div>
            </div>
          )}
          
          <div className="text-xs text-gray-400">
            {relevanceModelTrained ? (
              <>Accuracy: {(mlStatus?.status?.relevance?.accuracy * 100).toFixed(1)}% • v{mlStatus?.status?.relevance?.version}</>
            ) : (stats.total_feedback || 0) >= 500 ? (
              <>Ready to train! Run training script.</>
            ) : (
              <>Need {Math.max(0, 500 - (stats.total_feedback || 0))} more labels</>
            )}
          </div>
          <div className="text-[10px] text-gray-500 mt-1">
            Use "Not Buttah" / "Relevant" buttons
          </div>
        </div>

        {/* Sentiment Model */}
        <div className={`rounded-lg p-4 border ${sentimentModelTrained ? 'bg-green-900/20 border-green-700/50' : sentimentModelReady ? 'bg-amber-900/20 border-amber-700/50' : 'bg-gray-900/50 border-gray-700/50'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-white">Sentiment Classifier</span>
            {sentimentModelTrained ? (
              <span className="px-2 py-0.5 bg-green-900/50 text-green-400 text-xs rounded">Trained ✓</span>
            ) : sentimentModelReady ? (
              <span className="px-2 py-0.5 bg-amber-900/50 text-amber-400 text-xs rounded">Ready to Train</span>
            ) : (
              <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded">Collecting Data</span>
            )}
          </div>
          
          {/* Sentiment Progress Bar */}
          {!sentimentModelTrained && (
            <div className="mb-2">
              <div className="flex justify-between text-[10px] text-gray-500 mb-1">
                <span>{sentimentCorrections} / 200 corrections</span>
                <span>{Math.min(100, Math.round((sentimentCorrections / 200) * 100))}%</span>
              </div>
              <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    sentimentCorrections >= 200 ? 'bg-green-500' : 
                    sentimentCorrections >= 50 ? 'bg-amber-500' : 'bg-purple-500'
                  }`}
                  style={{ width: `${Math.min(100, (sentimentCorrections / 200) * 100)}%` }}
                />
              </div>
            </div>
          )}
          
          <div className="text-xs text-gray-400">
            {sentimentModelTrained ? (
              <>Accuracy: {(mlStatus?.status?.sentiment?.accuracy * 100).toFixed(1)}% • v{mlStatus?.status?.sentiment?.version}</>
            ) : sentimentCorrections >= 200 ? (
              <>Ready to train! Run training script.</>
            ) : (
              <>Need {Math.max(0, 200 - sentimentCorrections)} more corrections</>
            )}
          </div>
          <div className="text-[10px] text-gray-500 mt-1">
            Use sentiment dropdown to correct predictions
          </div>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setFilterRelevance('all')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            filterRelevance === 'all' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          All ({stats.total_feedback})
        </button>
        <button
          onClick={() => setFilterRelevance('relevant')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            filterRelevance === 'relevant' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          Relevant ({stats.relevant_count})
        </button>
        <button
          onClick={() => setFilterRelevance('irrelevant')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            filterRelevance === 'irrelevant' ? 'bg-red-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          Irrelevant ({stats.irrelevant_count})
        </button>
        <button
          onClick={() => setFilterRelevance('sentiment')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            filterRelevance === 'sentiment' ? 'bg-amber-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          Sentiment ({sentimentCorrections})
        </button>
      </div>

      {/* Recent Feedback (limited to 5) */}
      <div className="space-y-2">
        {filteredFeedback.slice(0, 5).map((item: any) => (
          <div key={item.id} className="bg-gray-900/50 rounded-lg p-3 text-sm">
            <div className="flex items-start justify-between gap-2">
              <p className="text-white flex-1 line-clamp-2">{item.raw_text || 'No text'}</p>
              <div className="flex items-center gap-2 shrink-0">
                {/* Sentiment correction badge */}
                {item.corrected_sentiment && (
                  <span className="px-2 py-0.5 bg-amber-900/30 border border-amber-700/50 rounded text-amber-300 text-xs whitespace-nowrap">
                    {item.original_sentiment} → {item.corrected_sentiment}
                  </span>
                )}
                {/* Relevance badge */}
                {item.is_relevant !== null && (
                  item.is_relevant ? (
                    <span className="px-2 py-0.5 bg-green-900/30 border border-green-700/50 rounded text-green-300 text-xs whitespace-nowrap">✓ Relevant</span>
                  ) : (
                    <span className="px-2 py-0.5 bg-red-900/30 border border-red-700/50 rounded text-red-300 text-xs whitespace-nowrap">✗ Irrelevant</span>
                  )
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
              <span>{item.source}</span>
              <span>•</span>
              <span>{new Date(item.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        ))}
      </div>

      {filteredFeedback.length > 5 && (
        <div className="mt-4 text-center">
          <a 
            href="/agents/social-listening" 
            className="text-sm text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"
          >
            View all {filteredFeedback.length} labels in Social Listening
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function IngestionConfig() {
  const { companyId: rawCompanyId } = useTenant();
  // Use Buttah's company ID as fallback if not loaded yet
  const companyId = rawCompanyId || '00000000-0000-0000-0000-000000000001';
  const queryClient = useQueryClient();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <GlobalHeader />
      
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl">
              <Database className="w-8 h-8 text-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Ingestion Configuration</h1>
              <p className="text-gray-400">Manage data sources for social listening and competitor intelligence</p>
            </div>
          </div>
          <button
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ['brandSources'] });
              queryClient.invalidateQueries({ queryKey: ['competitorSources'] });
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Info Banner */}
        <div className="mb-8 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
          <div className="flex items-start gap-3">
            <Zap className="w-5 h-5 text-blue-400 mt-0.5" />
            <div>
              <p className="text-blue-300 font-medium">No Code Changes Required</p>
              <p className="text-sm text-blue-400/70">
                Add or modify sources here and they'll be used automatically in the next ingestion run.
                No deployment needed.
              </p>
            </div>
          </div>
        </div>

        {/* Sync Now Section */}
        <SyncNowSection />

        {/* Config Sections */}
        <div className="space-y-8 mt-8">
          <BrandSourcesSection />
          <CompetitorSourcesSection />
          <FeedbackManagementSection />
        </div>
      </main>
    </div>
  );
}

